/**
 * OptiVehicle mileage — what the vehicle's odometer readings say, from the
 * records OptiBay already holds (no separate store, no estimate):
 *
 *   OptiBay visits   tickets.odometer_miles, observed at the visit's check-in
 *                    (ticket created); corrections keep the trail
 *                    (ticket_odometer_revisions) and are marked "corrected".
 *   Imported ROs     the previous system's repair orders linked to this vehicle
 *                    by exact VIN — EXTERNAL records, labelled as such;
 *                    odometer out (else in), observed at close (else open).
 *
 * The LATEST reading is the one with the newest observation date — never the
 * newest import — so an older imported record can never replace a newer
 * OptiBay reading. On the same instant an OptiBay reading wins.
 *
 * Questionable readings are FLAGGED for a person to check, never corrected and
 * never called fraud: a reading lower than an earlier one ("lower than an
 * earlier reading") or one implying more than LARGE_INCREASE_MILES_PER_DAY on
 * average since the previous reading ("unusually large increase"). Missing
 * mileage is simply absent.
 */
export type MileageSource = 'ticket' | 'imported';
export type MileageFlag = 'lower_than_earlier' | 'large_increase';

export interface MileageObservation {
  miles: number;
  observedAt: string;
  source: MileageSource;
  /** "Ticket #0042" / "Imported RO 1234" — what a person recognises. */
  label: string;
  ticketId?: string;
  /** An OptiBay reading that was edited after it was first entered. */
  corrected?: { previousMiles: number; changedAt: string };
  flag?: MileageFlag;
}

export interface VisitReading {
  ticketId: string;
  number: string;
  createdAt: string;
  odometerMiles: number | null;
}

export interface ImportedReading {
  id: string;
  roNumber: string | null;
  openedAt: string | null;
  closedAt: string | null;
  odometerIn: number | null;
  odometerOut: number | null;
}

export interface OdometerRevision {
  ticketId: string;
  previousMiles: number;
  newMiles: number | null;
  changedAt: string;
}

/** Average miles per day above which an increase is called "unusually large". */
export const LARGE_INCREASE_MILES_PER_DAY = 500;
/** …and only when the increase itself is at least this big (short gaps are noisy). */
export const LARGE_INCREASE_MIN_MILES = 10_000;

export const MILEAGE_FLAG_TEXT: Record<MileageFlag, string> = {
  lower_than_earlier: 'Lower than an earlier reading — confirm the odometer',
  large_increase: 'Unusually large increase since the previous reading — confirm the odometer',
};

const valid = (n: number | null | undefined): n is number => typeof n === 'number' && Number.isFinite(n) && n >= 0;
const time = (iso: string) => new Date(iso).getTime();

/** All readings, NEWEST FIRST, each flagged against the readings observed before it. */
export function mileageObservations(
  visits: readonly VisitReading[],
  imported: readonly ImportedReading[],
  revisions: readonly OdometerRevision[] = [],
): MileageObservation[] {
  const firstRevision = new Map<string, OdometerRevision>();
  for (const r of [...revisions].sort((a, b) => time(a.changedAt) - time(b.changedAt))) {
    if (!firstRevision.has(r.ticketId)) firstRevision.set(r.ticketId, r);
  }
  const obs: MileageObservation[] = [];
  for (const v of visits) {
    if (!valid(v.odometerMiles) || Number.isNaN(time(v.createdAt))) continue;
    const rev = firstRevision.get(v.ticketId);
    obs.push({
      miles: v.odometerMiles, observedAt: v.createdAt, source: 'ticket', label: `Ticket ${v.number}`, ticketId: v.ticketId,
      ...(rev ? { corrected: { previousMiles: rev.previousMiles, changedAt: rev.changedAt } } : {}),
    });
  }
  for (const r of imported) {
    const miles = valid(r.odometerOut) ? r.odometerOut : valid(r.odometerIn) ? r.odometerIn : null;
    const at = r.closedAt ?? r.openedAt;
    if (miles == null || !at || Number.isNaN(time(at))) continue;
    obs.push({ miles, observedAt: at, source: 'imported', label: r.roNumber ? `Imported RO ${r.roNumber}` : 'Imported repair order' });
  }
  // chronological, OptiBay before imported on the same instant (so OptiBay is "later" = wins)
  const chrono = obs.sort((a, b) => time(a.observedAt) - time(b.observedAt) || (a.source === b.source ? 0 : a.source === 'imported' ? -1 : 1));
  let maxSoFar: number | null = null;
  let prev: MileageObservation | null = null;
  for (const o of chrono) {
    if (maxSoFar != null && o.miles < maxSoFar) o.flag = 'lower_than_earlier';
    else if (prev) {
      const days = Math.max(1, (time(o.observedAt) - time(prev.observedAt)) / 86_400_000);
      const delta = o.miles - prev.miles;
      if (delta >= LARGE_INCREASE_MIN_MILES && delta / days > LARGE_INCREASE_MILES_PER_DAY) o.flag = 'large_increase';
    }
    maxSoFar = maxSoFar == null ? o.miles : Math.max(maxSoFar, o.miles);
    prev = o;
  }
  return chrono.reverse();
}

/** The most recently OBSERVED reading (not the most recently imported one). */
export function latestMileage(observations: readonly MileageObservation[]): MileageObservation | null {
  return observations[0] ?? null;
}

/** Entry-time check (Create Ticket): a lower entry is allowed, but worth a second look. */
export function entryBelowLatest(entered: number | null | undefined, latest: MileageObservation | null): boolean {
  return valid(entered) && entered > 0 && latest != null && entered < latest.miles;
}
